import type {
  DashboardFilters,
  PeriodMode,
  SalesRecord,
  SummaryMetrics,
  TargetConfig,
  TerminalRow,
  TrendPoint,
} from '../types';

export const ALL = '全部';

export const parseYmd = (value: string) => new Date(`${value}T12:00:00`);

export const toYmd = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const uniqueCount = (records: SalesRecord[]) =>
  new Set(records.map((record) => record.customerCode || record.customerName)).size;

const percent = (numerator: number, denominator: number) => (denominator ? (numerator / denominator) * 100 : 0);

export const getOptions = (records: SalesRecord[], key: keyof SalesRecord) =>
  [...new Set(records.map((record) => String(record[key] ?? '').trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'zh-CN'),
  );

export const filterRecords = (records: SalesRecord[], filters: DashboardFilters) =>
  records.filter((record) => {
    if (filters.startDate && record.saleDate < filters.startDate) return false;
    if (filters.endDate && record.saleDate > filters.endDate) return false;
    if (filters.province !== ALL && record.province !== filters.province) return false;
    if (filters.city !== ALL && record.city !== filters.city) return false;
    if (filters.brand !== ALL && record.brand !== filters.brand) return false;
    if (filters.product !== ALL && record.product !== filters.product) return false;
    if (filters.flowType !== ALL && record.flowType !== filters.flowType) return false;
    return true;
  });

export const periodBounds = (anchorDate: string, mode: PeriodMode) => {
  const anchor = parseYmd(anchorDate);
  const year = anchor.getFullYear();
  if (mode === 'year') return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
  if (mode === 'quarter') {
    const startMonth = Math.floor(anchor.getMonth() / 3) * 3;
    const start = new Date(year, startMonth, 1, 12);
    const end = new Date(year, startMonth + 3, 0, 12);
    return { startDate: toYmd(start), endDate: toYmd(end) };
  }
  const start = new Date(year, anchor.getMonth(), 1, 12);
  const end = new Date(year, anchor.getMonth() + 1, 0, 12);
  return { startDate: toYmd(start), endDate: toYmd(end) };
};

export const quickRange = (anchorDate: string, key: 'all' | '30d' | PeriodMode, minDate: string) => {
  if (key === 'all') return { startDate: minDate, endDate: anchorDate };
  if (key === '30d') {
    const start = parseYmd(anchorDate);
    start.setDate(start.getDate() - 29);
    return { startDate: toYmd(start), endDate: anchorDate };
  }
  const bounds = periodBounds(anchorDate, key);
  return { startDate: bounds.startDate, endDate: anchorDate };
};

const rawSummary = (records: SalesRecord[], targetProduct: string) => {
  const targetRecords = records.filter((record) => record.product === targetProduct);
  const terminals = uniqueCount(records);
  const targetTerminals = uniqueCount(targetRecords);
  return {
    sales: records.reduce((sum, record) => sum + record.internalAmount, 0),
    quantity: records.reduce((sum, record) => sum + record.quantity, 0),
    terminals,
    penetration: percent(targetTerminals, terminals),
    targetProductSales: targetRecords.reduce((sum, record) => sum + record.internalAmount, 0),
    targetProductQuantity: targetRecords.reduce((sum, record) => sum + record.quantity, 0),
    returns: records.filter((record) => record.quantity < 0 || record.movementType.includes('退')).length,
  };
};

const previousRange = (startDate: string, endDate: string) => {
  const start = parseYmd(startDate);
  const end = parseYmd(endDate);
  const span = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  const priorEnd = new Date(start);
  priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorStart.getDate() - span + 1);
  return { startDate: toYmd(priorStart), endDate: toYmd(priorEnd) };
};

export const summarize = (
  allRecords: SalesRecord[],
  filters: DashboardFilters,
  targetProduct: string,
): SummaryMetrics => {
  const current = rawSummary(filterRecords(allRecords, filters), targetProduct);
  const range = previousRange(filters.startDate, filters.endDate);
  const priorRecords = filterRecords(allRecords, { ...filters, ...range });
  const prior = rawSummary(priorRecords, targetProduct);
  const hasPrior = priorRecords.length > 0;
  return {
    ...current,
    priorSales: hasPrior ? prior.sales : null,
    priorQuantity: hasPrior ? prior.quantity : null,
    priorTerminals: hasPrior ? prior.terminals : null,
    priorPenetration: hasPrior ? prior.penetration : null,
  };
};

const startOfWeek = (dateText: string) => {
  const date = parseYmd(dateText);
  const weekday = date.getDay() || 7;
  date.setDate(date.getDate() - weekday + 1);
  return toYmd(date);
};

const bucketMode = (records: SalesRecord[]) => {
  if (!records.length) return 'day' as const;
  const dates = records.map((record) => record.saleDate).sort();
  const span = Math.round((parseYmd(dates.at(-1)!).getTime() - parseYmd(dates[0]).getTime()) / 86400000);
  if (span > 185) return 'month' as const;
  if (span > 62) return 'week' as const;
  return 'day' as const;
};

const bucketKey = (date: string, mode: 'day' | 'week' | 'month') => {
  if (mode === 'month') return date.slice(0, 7);
  if (mode === 'week') return startOfWeek(date);
  return date;
};

const bucketLabel = (key: string, mode: 'day' | 'week' | 'month') => {
  if (mode === 'month') return `${Number(key.slice(5, 7))}月`;
  if (mode === 'week') return `${Number(key.slice(5, 7))}/${Number(key.slice(8, 10))}周`;
  return `${Number(key.slice(5, 7))}/${Number(key.slice(8, 10))}`;
};

export const buildTrend = (records: SalesRecord[], targetProduct: string): TrendPoint[] => {
  const mode = bucketMode(records);
  const groups = new Map<string, SalesRecord[]>();
  records.forEach((record) => {
    const key = bucketKey(record.saleDate, mode);
    groups.set(key, [...(groups.get(key) ?? []), record]);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, rows]) => {
      const targets = rows.filter((row) => row.product === targetProduct);
      const terminals = uniqueCount(rows);
      const penetratedTerminals = uniqueCount(targets);
      return {
        key,
        label: bucketLabel(key, mode),
        sales: rows.reduce((sum, row) => sum + row.internalAmount, 0),
        quantity: rows.reduce((sum, row) => sum + row.quantity, 0),
        terminals,
        penetratedTerminals,
        penetration: percent(penetratedTerminals, terminals),
      };
    });
};

export interface CategoryTotal {
  name: string;
  sales: number;
  quantity: number;
  terminals: number;
  share: number;
}

export const categoryTotals = (records: SalesRecord[], key: keyof SalesRecord): CategoryTotal[] => {
  const groups = new Map<string, SalesRecord[]>();
  records.forEach((record) => {
    const name = String(record[key] || '未分类');
    groups.set(name, [...(groups.get(name) ?? []), record]);
  });
  const total = records.reduce((sum, record) => sum + record.internalAmount, 0);
  return [...groups.entries()]
    .map(([name, rows]) => {
      const sales = rows.reduce((sum, row) => sum + row.internalAmount, 0);
      return {
        name,
        sales,
        quantity: rows.reduce((sum, row) => sum + row.quantity, 0),
        terminals: uniqueCount(rows),
        share: percent(sales, total),
      };
    })
    .sort((a, b) => b.sales - a.sales);
};

export interface PenetrationRow {
  province: string;
  city: string;
  totalTerminals: number;
  targetTerminals: number;
  penetration: number;
  targetSales: number;
  totalSales: number;
  gap: number;
}

export const penetrationByGeography = (records: SalesRecord[], targetProduct: string) => {
  const groups = new Map<string, SalesRecord[]>();
  records.forEach((record) => {
    const key = `${record.province || '未标注'}\u0000${record.city || '未标注'}`;
    groups.set(key, [...(groups.get(key) ?? []), record]);
  });
  return [...groups.entries()]
    .map(([key, rows]): PenetrationRow => {
      const [province, city] = key.split('\u0000');
      const targetRows = rows.filter((row) => row.product === targetProduct);
      const totalTerminals = uniqueCount(rows);
      const targetTerminals = uniqueCount(targetRows);
      return {
        province,
        city,
        totalTerminals,
        targetTerminals,
        penetration: percent(targetTerminals, totalTerminals),
        targetSales: targetRows.reduce((sum, row) => sum + row.internalAmount, 0),
        totalSales: rows.reduce((sum, row) => sum + row.internalAmount, 0),
        gap: Math.max(0, totalTerminals - targetTerminals),
      };
    })
    .sort((a, b) => b.penetration - a.penetration || b.totalSales - a.totalSales);
};

export const terminalRows = (records: SalesRecord[], targetProduct: string): TerminalRow[] => {
  const groups = new Map<string, SalesRecord[]>();
  records.forEach((record) => {
    const key = record.customerCode || record.customerName;
    groups.set(key, [...(groups.get(key) ?? []), record]);
  });
  return [...groups.entries()]
    .map(([customerCode, rows]) => ({
      customerCode,
      customerName: rows[0].customerName,
      province: rows[0].province,
      city: rows[0].city,
      flowType: rows[0].flowType,
      sales: rows.reduce((sum, row) => sum + row.internalAmount, 0),
      quantity: rows.reduce((sum, row) => sum + row.quantity, 0),
      productCount: new Set(rows.map((row) => row.product)).size,
      lastPurchase: rows.map((row) => row.saleDate).sort().at(-1) ?? '',
      hasTargetProduct: rows.some((row) => row.product === targetProduct),
    }))
    .sort((a, b) => b.sales - a.sales);
};

export interface CategoryTrendResult {
  points: TrendPoint[];
  categories: string[];
}

export const categoryTrend = (records: SalesRecord[], key: keyof SalesRecord): CategoryTrendResult => {
  const mode = bucketMode(records);
  const categories = categoryTotals(records, key).slice(0, 7).map((item) => item.name);
  const groups = new Map<string, SalesRecord[]>();
  records.forEach((record) => {
    const dateKey = bucketKey(record.saleDate, mode);
    groups.set(dateKey, [...(groups.get(dateKey) ?? []), record]);
  });
  const points = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, rows]) => {
      const point: TrendPoint = {
        key: dateKey,
        label: bucketLabel(dateKey, mode),
        sales: rows.reduce((sum, row) => sum + row.internalAmount, 0),
        quantity: rows.reduce((sum, row) => sum + row.quantity, 0),
        terminals: uniqueCount(rows),
        penetratedTerminals: 0,
        penetration: 0,
      };
      categories.forEach((category) => {
        point[category] = rows
          .filter((row) => String(row[key] || '未分类') === category)
          .reduce((sum, row) => sum + row.internalAmount, 0);
      });
      return point;
    });
  return { points, categories };
};

export const targetForPeriod = (annualTarget: number, mode: PeriodMode) => {
  if (mode === 'month') return annualTarget / 12;
  if (mode === 'quarter') return annualTarget / 4;
  return annualTarget;
};

export interface Insight {
  tone: 'positive' | 'warning' | 'neutral';
  title: string;
  detail: string;
}

export const buildInsights = (
  records: SalesRecord[],
  summary: SummaryMetrics,
  targets: TargetConfig,
): Insight[] => {
  if (!records.length) return [{ tone: 'warning', title: '当前筛选暂无数据', detail: '请调整日期或维度筛选后重新查看。' }];
  const products = categoryTotals(records, 'product');
  const provinces = categoryTotals(records, 'province');
  const topProduct = products[0];
  const topProvince = provinces[0];
  const salesChange = summary.priorSales && summary.priorSales !== 0
    ? ((summary.sales - summary.priorSales) / Math.abs(summary.priorSales)) * 100
    : null;
  const insights: Insight[] = [];
  if (salesChange !== null) {
    insights.push({
      tone: salesChange >= 0 ? 'positive' : 'warning',
      title: `销售额较上一等长周期${salesChange >= 0 ? '增长' : '下降'} ${Math.abs(salesChange).toFixed(1)}%`,
      detail: salesChange >= 0 ? '增长动能延续，建议复盘贡献最大的区域与终端。' : '建议优先检查下滑区域、退货与重点终端缺口。',
    });
  } else {
    insights.push({ tone: 'neutral', title: '暂缺可比上期数据', detail: '继续按周追加数据后，将自动形成环比趋势。' });
  }
  if (topProduct) {
    insights.push({
      tone: 'positive',
      title: `${topProduct.name} 贡献最高`,
      detail: `贡献当前销售额的 ${topProduct.share.toFixed(1)}%，可作为本期资源倾斜的核心单品。`,
    });
  }
  if (summary.penetration < 50) {
    insights.push({
      tone: 'warning',
      title: `${targets.targetProduct} 渗透率为 ${summary.penetration.toFixed(1)}%`,
      detail: `仍有 ${Math.max(0, summary.terminals - Math.round((summary.penetration / 100) * summary.terminals))} 家活跃终端未覆盖，建议从高销售未渗透终端开始。`,
    });
  } else if (topProvince) {
    insights.push({
      tone: 'positive',
      title: `${topProvince.name} 是主要贡献区域`,
      detail: `贡献当前销售额的 ${topProvince.share.toFixed(1)}%，建议复制其终端覆盖打法。`,
    });
  }
  if (summary.returns > 0) {
    insights.push({
      tone: 'warning',
      title: `检测到 ${summary.returns} 笔退货或负数记录`,
      detail: '看板已按净额计入，建议在明细页核对对应终端和单品。',
    });
  }
  return insights.slice(0, 4);
};
